import React, { useState, useRef } from 'react';
import { Paper, Typography, Grid, Button, IconButton, Box, Alert, Snackbar } from '@mui/material';
import { Delete, CloudUpload } from '@mui/icons-material';
import { ArtworkImageStub } from "../../models/Artwork";
import validateImageFiles, { ValidationResult, validateFileSignature }  from "./validateImageFiles";

// Type definitions
interface ImageFile extends File {
    preview?: string;
}


interface ImageUploadProps {
    // Core state props
    value?: ImageFile[];
    existingImages?: ArtworkImageStub[];
    onImagesChange?: (files: ImageFile[], validationResult: ValidationResult | null) => void;
    onExistingImagesChange?: (images: ArtworkImageStub[]) => void;
    onRemoveImage: (id: number) => void;

    // Configuration props
    multiple?: boolean;
    maxFiles?: number;
    maxFileSize?: number; // in bytes
    accept?: string;

    // UI customization
    elevation?: number;
    thumbnailSize?: number;
    showFileList?: boolean;
    showThumbnails?: boolean;
    disabled?: boolean;

    // Labels
    uploadLabel?: string;
    fileListLabel?: string;
    thumbnailsLabel?: string;

    // Validation
    onValidationError?: (error: { type: 'SIZE' | 'COUNT' | 'TYPE'; message: string }) => void;
}

const ImageUploadManager: React.FC<ImageUploadProps> = ({
    // State
    value = [],
    existingImages = [],
    onImagesChange,
    onExistingImagesChange,
    onRemoveImage,
    // Configuration
    multiple = true,
    maxFiles,
    maxFileSize = 10 * 1024 * 1024, // 10MB default
    accept = "image/*",

    // UI
    elevation = 2,
    thumbnailSize = 100,
    showFileList = true,
    showThumbnails = true,
    disabled = false,

    // Labels
    uploadLabel = "Drag & drop images here or click to browse",
    fileListLabel = "Selected Files",
    thumbnailsLabel = "Image Previews",

    // Validation
    onValidationError,
}) => {
    const [internalFiles, setInternalFiles] = useState<ImageFile[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [showAlert, setShowAlert] = useState(false);
    const uploadedImages = internalFiles;

    const setUploadedImages = (files: ImageFile[]) => {
        setInternalFiles(files);
        onImagesChange?.(files, null);
    };

    const validateFile = (file: File): boolean => {
        if (maxFileSize && file.size > maxFileSize) {
            onValidationError?.({
                type: 'SIZE',
                message: `File ${file.name} exceeds maximum size of ${(maxFileSize / 1024 / 1024).toFixed(1)}MB`
            });
            return false;
        }

        if (accept !== "image/*") {
            const acceptedTypes = accept.split(',').map(type => type.trim());
            const fileType = file.type;
            const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

            const isValidType = acceptedTypes.some(type =>
                type === fileType ||
                type === `.${fileType.split('/')[1]}` ||
                type === fileExtension
            );

            if (!isValidType) {
                onValidationError?.({
                    type: 'TYPE',
                    message: `File ${file.name} is not an accepted type`
                });
                return false;
            }
        }

        return true;
    };

    const handleImageChange = async (files: FileList | null) => {
        if (!files || files.length === 0) return;

        // Basic validation
        const validationResult = validateImageFiles(files, {
            maxFileSize: 5 * 1024 * 1024, // 5MB
            maxFiles: 10,
            allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
        });

        // Advanced signature validation for suspicious files
        const advancedValidation = await Promise.all(
            validationResult.validFiles.map(async (file) => {
                const isValidSignature = await validateFileSignature(file);
                return { file, isValidSignature };
            })
        );

        const finallyValidFiles = advancedValidation
            .filter(result => result.isValidSignature)
            .map(result => result.file);

        // Update errors for files that failed signature validation
        const signatureErrors = advancedValidation
            .filter(result => !result.isValidSignature)
            .map(result => `"${result.file.name}" failed security validation.`);

        const allErrors = [...validationResult.errors, ...signatureErrors];

        if (allErrors.length > 0) {
            setValidationErrors(allErrors);
            setShowAlert(true);
        }

        // Only pass valid files to parent component
        const finalValidationResult = {
            isValid: allErrors.length === 0 && finallyValidFiles.length > 0,
            errors: allErrors,
            validFiles: finallyValidFiles
        };

        let filesArray = Array.from(finallyValidFiles) as ImageFile[];
        const newUploadedImages = multiple ? [...uploadedImages, ...filesArray] : [filesArray[0]];
        setUploadedImages(newUploadedImages);

        onImagesChange?.(newUploadedImages, finalValidationResult);
    };


    const handleImageChangeOld = (files: FileList | null) => {
        if (!files || files.length === 0) return;

        let filesArray = Array.from(files) as ImageFile[];

        // Validate files
        filesArray = filesArray.filter(validateFile);
        if (filesArray.length === 0) return;

        // Apply maxFiles limit
        const currentCount = uploadedImages.length;
        if (maxFiles && (currentCount + filesArray.length) > maxFiles) {
            const allowed = maxFiles - currentCount;
            filesArray = filesArray.slice(0, allowed);
            onValidationError?.({
                type: 'COUNT',
                message: `Maximum ${maxFiles} files allowed. Only ${allowed} additional files were selected.`
            });
        }

        // Create preview URLs
        filesArray = filesArray.map(file => ({
            ...file,
            preview: URL.createObjectURL(file)
        }));

        const newUploadedImages = multiple ? [...uploadedImages, ...filesArray] : [filesArray[0]];
        setUploadedImages(newUploadedImages);
    };

    const handleRemoveUploaded = (index: number) => {
        const fileToRemove = uploadedImages[index];

        // Clean up preview URL
        if (fileToRemove.preview) {
            URL.revokeObjectURL(fileToRemove.preview);
        }

        const newUploadedImages = uploadedImages.filter((_, i) => i !== index);
        setUploadedImages(newUploadedImages);
    };

    const handleRemoveExisting = (imageId: number) => {
        const newExistingImages = existingImages.filter(img => img.imageId !== imageId);
        onRemoveImage(imageId);
        onExistingImagesChange?.(newExistingImages);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        if (disabled) return;
        handleImageChange(e.dataTransfer.files);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
    };

    const triggerFileInput = () => {
        if (disabled) return;
        fileInputRef.current?.click();
    };

    const clearAllUploads = () => {
        // Clean up all preview URLs
        uploadedImages.forEach(file => {
            if (file.preview) {
                URL.revokeObjectURL(file.preview);
            }
        });
        setUploadedImages([]);
    };

    // Clean up preview URLs on unmount
    React.useEffect(() => {
        return () => {
            uploadedImages.forEach(file => {
                if (file.preview) {
                    URL.revokeObjectURL(file.preview);
                }
            });
        };
    }, []);

    const totalFilesCount = uploadedImages.length;
    const hasFiles = totalFilesCount > 0 || existingImages.length > 0;

    return (
        <>
        <Paper
            elevation={elevation}
            sx={{
                p: 2,
                opacity: disabled ? 0.6 : 1,
                pointerEvents: disabled ? 'none' : 'auto'
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
        >
            {/* Upload Area */}
            <Box
                sx={{
                    border: '2px dashed',
                    borderColor: disabled ? 'grey.300' : 'grey.400',
                    borderRadius: 1,
                    p: 3,
                    textAlign: 'center',
                    cursor: disabled ? 'default' : 'pointer',
                    backgroundColor: disabled ? 'action.disabledBackground' : 'background.paper',
                    '&:hover': disabled ? {} : {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover'
                    }
                }}
                onClick={triggerFileInput}
            >
                <CloudUpload sx={{
                    fontSize: 40,
                    color: disabled ? 'grey.400' : 'grey.500',
                    mb: 1
                }} />
                <Typography
                    variant="body2"
                    color={disabled ? 'text.disabled' : 'textSecondary'}
                    gutterBottom
                >
                    {uploadLabel}
                </Typography>
                <Typography variant="caption" color={disabled ? 'text.disabled' : 'textSecondary'}>
                    {accept} {maxFiles && `• Max ${maxFiles} files`} {maxFileSize && `• Max ${(maxFileSize / 1024 / 1024).toFixed(0)}MB each`}
                </Typography>
            </Box>

            {/* Hidden File Input */}
            <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                multiple={multiple}
                onChange={(e) => handleImageChange(e.target.files)}
                style={{ display: 'none' }}
                disabled={disabled}
            />

            {/* File List */}
            {showFileList && totalFilesCount > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        {fileListLabel} ({totalFilesCount}{maxFiles && ` / ${maxFiles}`})
                    </Typography>
                    <Box sx={{ maxHeight: 120, overflow: 'auto' }}>
                        {uploadedImages.map((file, idx) => (
                            <Box
                                key={`${file.name}-${idx}`}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    py: 0.5,
                                    px: 1,
                                    '&:hover': { backgroundColor: 'action.hover' }
                                }}
                            >
                                <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                                    {file.name}
                                </Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ mx: 1 }}>
                                    {(file.size / 1024 / 1024).toFixed(2)} MB
                                </Typography>
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveUploaded(idx);
                                    }}
                                    disabled={disabled}
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                    {totalFilesCount > 0 && (
                        <Button
                            size="small"
                            color="error"
                            onClick={clearAllUploads}
                            sx={{ mt: 1 }}
                            disabled={disabled}
                        >
                            Clear All Uploads
                        </Button>
                    )}
                </Box>
            )}

            {/* Thumbnails */}
            {showThumbnails && hasFiles && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        {thumbnailsLabel}
                    </Typography>
                    <Grid container spacing={1}>
                        {/* Existing Images */}
                        {existingImages.map((img) => (
                            <Grid key={img.imageId}>
                                <Box sx={{ position: 'relative' }}>
                                    <img
                                        src={img.imageUrl}
                                        alt={'Existing image'}
                                        width={thumbnailSize}
                                        height={thumbnailSize}
                                        style={{
                                            objectFit: 'cover',
                                            borderRadius: 4,
                                        }}
                                    />
                                    {onExistingImagesChange && (
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleRemoveExisting(img.imageId)}
                                            disabled={disabled}
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                right: 0,
                                                backgroundColor: 'background.paper',
                                                '&:hover': { backgroundColor: 'error.light' },
                                                minWidth: '24px',
                                                width: '24px',
                                                height: '24px'
                                            }}
                                        >
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                            </Grid>
                        ))}

                        {/* Uploaded Image Previews */}
                        {uploadedImages.map((file, idx) => (
                            <Grid key={`upload-${idx}`}>
                                <Box sx={{ position: 'relative' }}>
                                    <img
                                        src={file.preview || URL.createObjectURL(file)}
                                        alt={`Preview ${idx + 1}`}
                                        width={thumbnailSize}
                                        height={thumbnailSize}
                                        style={{
                                            objectFit: 'cover',
                                            borderRadius: 4,
                                        }}
                                    />
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleRemoveUploaded(idx)}
                                        disabled={disabled}
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            right: 0,
                                            backgroundColor: 'background.paper',
                                            '&:hover': { backgroundColor: 'error.light' },
                                            minWidth: '24px',
                                            width: '24px',
                                            height: '24px'
                                        }}
                                    >
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}
            </Paper>
            <Snackbar
                open={showAlert}
                autoHideDuration={6000}
                onClose={() => setShowAlert(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert severity="error" onClose={() => setShowAlert(false)}>
                    <Box>
                        <strong>Invalid files detected:</strong>
                        {validationErrors.map((error, index) => (
                            <div key={index} style={{ fontSize: '0.875rem', marginTop: 4 }}>
                                • {error}
                            </div>
                        ))}
                    </Box>
                </Alert>
            </Snackbar>
        </>
    );
};

export default ImageUploadManager;